import {
  SelectProps,
  Select,
  Popover,
  Space,
} from 'antd';
import { DefaultOptionType, BaseOptionType } from 'antd/lib/select';
import clsx from 'classnames';
import { BaseSelectRef } from 'rc-select';
import React, { FC, useState, useEffect } from 'react';
import './index.scss';

const CheckoutSvg = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="16" height="16" rx="2" fill="#0A6CFF" />
    <path fillRule="evenodd" clipRule="evenodd" d="M3.59029 7.91195C3.90107 7.60974 4.39591 7.60974 4.70668 7.91195L6.78168 9.92974L11.2933 5.54278C11.6041 5.24058 12.0989 5.24059 12.4097 5.5428C12.733 5.8572 12.733 6.37646 12.4097 6.69085L7.06897 11.8843C6.91039 12.0386 6.6533 12.0386 6.49472 11.8843L3.59029 9.05999C3.26699 8.7456 3.26699 8.22634 3.59029 7.91195Z" fill="white" />
  </svg>
);

const RemoveSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path fillRule="evenodd" clipRule="evenodd" d="M10.4059 11.2511L11.2539 10.4031L8.84511 7.99433L11.248 5.59144L10.4029 4.74638L8.00006 7.14927L5.597 4.74622L4.74903 5.5942L7.15208 7.99725L4.74609 10.4032L5.59114 11.2483L7.99713 8.8423L10.4059 11.2511Z" fill="#7B889C"/>
</svg>
);

const DownSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M7.60391 10.4539C7.8226 10.6726 8.17718 10.6726 8.39588 10.4539L12.5 6.3497L11.6488 5.49849L7.99771 9.14956L4.34906 5.50083L3.5 6.34989L7.60391 10.4539Z" fill="#4A5970" />
  </svg>
);

export const Mark: FC<{ name: any; keyword: string }> = ({ name, keyword }) => {
  if (!keyword || typeof name !== 'string') {
    return <>{name}</>;
  }
  const arr = name?.split(keyword);
  return (
    <>
      {arr?.map((str, index) => (
        <span key={index}>
          {str}
          {index === arr.length - 1 ? null : (
            <span style={{ color: '#0A6CFF' }}>{keyword}</span>
          )}
        </span>
      ))}
    </>
  );
};

export interface TdsSelectProps<ValueType = any, OptionType extends BaseOptionType = DefaultOptionType>
  extends SelectProps<ValueType, OptionType> {
  checkbox?: boolean;
  renderCheckboxLabel?: (option: OptionType) => React.ReactNode;
}

export const TdsSelect = <ValueType extends any = any, OptionType extends BaseOptionType = DefaultOptionType>
  (
    props: TdsSelectProps<ValueType, OptionType> & {
      ref?: React.Ref<BaseSelectRef> | undefined,
    },
  ) => {
  const {
    onSearch,
    onBlur,
    options,
    filterOption,
    className,
    checkbox,
    mode,
    renderCheckboxLabel,
    children,
    tagRender,
    dropdownClassName,
    ...rest
  } = props;
  const [keyword, setKeyword] = useState('');
  const [sortOptions, setSortOptions] = useState<OptionType[]>([]);

  const handleSearch = (value: string) => {
    setKeyword(value);
    const sortOptions = [...(options || [])];
    sortOptions?.sort((a, b) => {
      const aIndex = String(a.label).toLowerCase()
        .indexOf(value);
      const bIndex = String(b.label).toLowerCase()
        .indexOf(value);

      if (aIndex === -1 && bIndex === -1) {
        return 0;
      }

      if (aIndex === -1) {
        return 1;
      }

      if (bIndex === -1) {
        return -1;
      }

      return aIndex - bIndex;
    });

    setSortOptions(sortOptions);
    onSearch?.(value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    setKeyword('');
    onBlur?.(e);
  };

  const handleFilterOption = (keyword: string, option?: OptionType) => {
    if (option?.options?.length) {
      const current = option?.options?.find((item: DefaultOptionType) => item?.value === option?.value);
      return `${current?.label}`?.includes(keyword) || `${current?.value}`.includes(keyword);
    }
    return `${option?.label}`?.includes(keyword) || `${option?.value}`.includes(keyword);
  };

  const renderOption = (option: OptionType) => {
    const label = option.label || option.value;
    if (checkbox) {
      return <Select.Option key={option.value} title={label} className='tds-checkbox-select-option' {...option}>
        {
          mode === 'multiple' || mode === 'tags' ? <div className='tds-checkbox-select-icon-wrapper'>
            <div className='tds-checkbox-select-icon'>
              <CheckoutSvg />
            </div>
          </div> : null
        }
        {renderCheckboxLabel ? renderCheckboxLabel?.(option) : label}
      </Select.Option>;
    }
    return <Select.Option key={option.value} title={label} {...option}>
      <Mark name={label} keyword={keyword} />
    </Select.Option>;
  };

  useEffect(() => {
    setSortOptions(options || []);
  }, [options]);

  return <Select
    filterOption={filterOption !== undefined ? filterOption : handleFilterOption}
    onSearch={handleSearch}
    onBlur={handleBlur}
    showSearch
    showArrow
    className={clsx('tds-select', className)}
    mode={mode}
    removeIcon={<div className='tds-select-remove-icon'><RemoveSvg /></div>}
    suffixIcon={<DownSvg />}
    dropdownClassName={clsx(dropdownClassName, 'tds-select-dropdown')}
    maxTagPlaceholder={omittedValues => (
      <Popover
        overlayStyle={{ maxWidth: '500px' }}
        content={<Space wrap={true}>
          {
            omittedValues.map((item, index) => (
              <div key={index} className='tds-select-max-tag-placeholder-popver'>
                {tagRender ? tagRender({
                  label: item.label,
                  value: item.value,
                  disabled: item.disabled || false,
                  onClose: () => { },
                  closable: false,
                }) : item.label}
                {(index === omittedValues.length - 1 || tagRender) ? '  ' : ', '}
              </div>
            ))
          }
        </Space>}
      >
        <span>+ {omittedValues.length} ...</span>
      </Popover>
    )}
    tagRender={tagRender}
    {...rest}
  >
    {
      sortOptions?.map((item) => {
        if (item.options) {
          return (
            <Select.OptGroup key={item.value} label={item.label}>
              {item.options?.map((option: OptionType) => renderOption(option))}
            </Select.OptGroup>
          );
        }
        return renderOption(item);
      })
    }
    {!sortOptions?.length ? children : null}
  </Select>;
};
